import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useCompanyOptions } from '../../hooks/useCompanyOptions';
import { supabase } from '../../lib/supabase';
import type { NewContact } from '../../types/contact';

interface ContactFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export default function ContactForm({ onSuccess, onCancel }: ContactFormProps) {
  const { user } = useAuth();
  const { options: companyOptions } = useCompanyOptions();
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<NewContact>({
    first_name: '',
    last_name: '',
    work_email: '',
    personal_email: '',
    company_id: '',
    job_title: '',
    linkedin_url: '',
    phone_number: '',
    birthday: '',
    significant_other: '',
    children_details: '',
    notes: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setSaving(true);
    setError(null);

    try {
      const { error: saveError } = await supabase
        .from('nrm_contacts')
        .insert([{ 
          ...formData,
          company_id: formData.company_id || null,
          birthday: formData.birthday || null,
          user_id: user.id 
        }]);

      if (saveError) throw saveError;

      onSuccess();
    } catch (err) {
      setError('Failed to save contact. Please try again.');
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleAddAnother = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setSaving(true);
    setError(null);

    try {
      const { error: saveError } = await supabase
        .from('nrm_contacts')
        .insert([{ 
          ...formData,
          company_id: formData.company_id || null,
          birthday: formData.birthday || null,
          user_id: user.id 
        }]);

      if (saveError) throw saveError;

      setFormData({
        first_name: '',
        last_name: '',
        work_email: '',
        personal_email: '',
        company_id: '',
        job_title: '',
        linkedin_url: '',
        phone_number: '',
        birthday: '',
        significant_other: '',
        children_details: '',
        notes: ''
      });
    } catch (err) {
      setError('Failed to save contact. Please try again.');
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label htmlFor="first_name" className="block text-sm font-medium text-gray-300 mb-1">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              required
              value={formData.first_name}
              onChange={handleChange}
              className="input w-full"
              placeholder="Enter first name"
            />
          </div>

          <div>
            <label htmlFor="last_name" className="block text-sm font-medium text-gray-300 mb-1">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              required
              value={formData.last_name}
              onChange={handleChange}
              className="input w-full"
              placeholder="Enter last name"
            />
          </div>

          <div>
            <label htmlFor="personal_email" className="block text-sm font-medium text-gray-300 mb-1">
              Personal Email
            </label>
            <input
              type="email"
              id="personal_email"
              name="personal_email"
              value={formData.personal_email}
              onChange={handleChange}
              className="input w-full"
              placeholder="Enter personal email"
            />
          </div>

          <div>
            <label htmlFor="work_email" className="block text-sm font-medium text-gray-300 mb-1">
              Work Email
            </label>
            <input
              type="email"
              id="work_email"
              name="work_email"
              value={formData.work_email}
              onChange={handleChange}
              className="input w-full"
              placeholder="Enter work email"
            />
          </div>

          <div>
            <label htmlFor="phone_number" className="block text-sm font-medium text-gray-300 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              id="phone_number"
              name="phone_number"
              value={formData.phone_number || ''}
              onChange={handleChange}
              className="input w-full"
              placeholder="Enter phone number"
            />
          </div>

          {!formData.work_email && !formData.personal_email && (
            <div className="text-yellow-500 text-sm">
              At least one email address (personal or work) is recommended for communication tracking
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="company_id" className="block text-sm font-medium text-gray-300 mb-1">
              Company
            </label>
            <select
              id="company_id"
              name="company_id"
              value={formData.company_id}
              onChange={handleChange}
              className="input w-full"
            >
              <option value="">Select company...</option>
              {companyOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="job_title" className="block text-sm font-medium text-gray-300 mb-1">
              Job Title
            </label>
            <input
              type="text"
              id="job_title"
              name="job_title"
              value={formData.job_title}
              onChange={handleChange}
              className="input w-full"
              placeholder="Enter job title"
            />
          </div>

          <div>
            <label htmlFor="linkedin_url" className="block text-sm font-medium text-gray-300 mb-1">
              LinkedIn URL
            </label>
            <input
              type="url"
              id="linkedin_url"
              name="linkedin_url"
              value={formData.linkedin_url}
              onChange={handleChange}
              className="input w-full"
              placeholder="Enter LinkedIn profile URL"
            />
          </div>

          <div>
            <label htmlFor="birthday" className="block text-sm font-medium text-gray-300 mb-1">
              Birthday
            </label>
            <input
              type="date"
              id="birthday"
              name="birthday"
              value={formData.birthday || ''}
              onChange={handleChange}
              className="input w-full"
            />
          </div>

          <div>
            <label htmlFor="significant_other" className="block text-sm font-medium text-gray-300 mb-1">
              Significant Other
            </label>
            <input
              type="text"
              id="significant_other"
              name="significant_other"
              value={formData.significant_other || ''}
              onChange={handleChange}
              className="input w-full"
              placeholder="Enter significant other's name"
            />
          </div>

          <div>
            <label htmlFor="children_details" className="block text-sm font-medium text-gray-300 mb-1">
              Children Details
            </label>
            <input
              type="text"
              id="children_details"
              name="children_details"
              value={formData.children_details || ''}
              onChange={handleChange}
              className="input w-full"
              placeholder="Enter children details"
            />
          </div>

          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-300 mb-1">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes || ''}
              onChange={handleChange}
              className="input w-full"
              rows={3}
              placeholder="Enter any additional notes"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="text-red-500 text-sm">
          {error}
        </div>
      )}

      <div className="flex justify-end space-x-4">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleAddAnother}
          className="btn btn-secondary"
          disabled={saving}
        >
          Save & Add Another
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Contact'}
        </button>
      </div>
    </form>
  );
}